import {
  ComponentProps,
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import Animated, {
  Easing,
  ReduceMotion,
  useAnimatedStyle,
  withTiming,
} from "react-native-reanimated";
import { View } from "tamagui";

import { Button } from "../stories/ui/button";

interface CollapsibleAreaContext {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

const CollapsibleAreaContext = createContext<CollapsibleAreaContext>({
  open: false,
  setOpen: () => console.warn("Should be used in CollapsibleAreaProvider"),
});

const useCollapsible = () => {
  const context = useContext(CollapsibleAreaContext);
  if (!context) {
    throw new Error("Should be used in CollapsibleAreaProvider");
  }

  const toggle = () => context.setOpen((prev) => !prev);

  return {
    toggle,
    ...context,
  };
};

const CollapsibleAreaProvider: React.FC<
  PropsWithChildren & {
    open?: boolean;
    onOpenChange?: (state: boolean) => void;
  }
> = ({ children, open = false, onOpenChange }) => {
  const [isOpen, setOpen] = useState(open);

  const onChange = useCallback(
    (value: boolean) => onOpenChange?.(value),
    [onOpenChange],
  );

  useEffect(() => {
    onChange(isOpen);
  }, [onChange, isOpen]);

  return (
    <CollapsibleAreaContext.Provider
      value={{
        open: isOpen,
        setOpen: setOpen,
      }}
    >
      {children}
    </CollapsibleAreaContext.Provider>
  );
};

const CollapsibleArea: React.FC<PropsWithChildren> = ({ children }) => {
  const { open } = useCollapsible();
  const [height, setHeight] = useState(0);

  const animated = useAnimatedStyle(() => {
    return {
      height: withTiming(open ? height : 0, {
        duration: 300,
        reduceMotion: ReduceMotion.Never,
        easing: Easing.inOut(Easing.linear),
      }),
    };
  });

  return (
    <Animated.View style={[{ overflow: "hidden" }, animated]}>
      <View onLayout={(e) => setHeight(e.nativeEvent.layout.height)}>
        {children}
      </View>
    </Animated.View>
  );
};

type ToggleProps = ComponentProps<typeof Button>;

const Toggle: React.FC<ToggleProps> = ({ children, ...rest }) => {
  const { toggle } = useCollapsible();
  return (
    <Button onPress={toggle} {...rest}>
      {children}
    </Button>
  );
};

export const Collapsible = {
  Area: CollapsibleArea,
  Toggle,
  Provider: CollapsibleAreaProvider,
};
